import L from "leaflet";
import { SelectiveWms } from "./SelectiveWms";
import {
  MAPA_CAPA_POLIGONOS_PADRE,
  MAPA_CAPA_VERTICES,
  fx_urlMapaRegistroFinca,
  ticketMapaRegistro,
} from "./MapaRegistroFinca";

/** Datos del filtro de una capa, como parámetros sueltos del GetMap. La Api
 *  los valida (entero, uuid) y arma el filtro con el registro del ticket. */
type FiltroMapa = Record<string, string>;

/** Capa WMS con los POLÍGONOS PADRE ya guardados del registro en curso.
 *
 *  **Una sola capa para toda la plataforma.** El registro lo pone la Api en
 *  cada petición, a partir del ticket (ver `MapaRegistroFinca`): miles de
 *  clientes dibujando a la vez comparten la misma capa publicada y cada uno ve
 *  la suya. Nada hay que configurar en GeoServer cuando entra un cliente nuevo.
 *
 *  **Va acumulando.** La capa devuelve TODOS los polígonos padre del registro,
 *  así que después del primero muestra uno, después del segundo dos, y así. No
 *  hay que agregarle nada por polígono: al guardarse en la base ya está adentro
 *  del filtro.
 *
 *  **El estilo lo pone GeoServer**, no el cliente: `sextante_poligono_padre`
 *  quedó como estilo por defecto de la capa y es el port exacto de
 *  `POLYGON_STYLE` (#E0490D, borde 3 px, relleno 25%). Por eso el GetMap no
 *  manda `styles`.
 *
 *  **Esta capa es el ÚNICO renderizador de una pieza guardada.** Antes el paso
 *  3 además la dibujaba con Leaflet, con un estilo idéntico, así que cada pieza
 *  se pintaba dos veces. Las piezas ya no son capas de Leaflet (ver
 *  `AoiPiezasState`): lo que Leaflet dibuja es sólo lo que todavía no está en
 *  la base.
 *
 *  **Las otras dos capas que monta.**
 *   - `_vertices`: los vértices de la UNA pieza que se está editando, filtrados
 *     por `poligono_padre_id`. Nace apagada y la encienden `mostrarVertices` /
 *     `ocultarVertices`.
 *   - `_seleccion`: la pieza elegida para borrar, en rojo. Es la misma capa de
 *     polígonos padre con otro SLD y filtrada a una fila.
 *
 *  **El TRAZO EN CURSO ya no sale de acá (2026-10-23).** Montaba dos capas
 *  más para dibujarlo: `_verticesTrazo` (los puntos, de
 *  `tbl_temp_vertices_dibujo` con `trazo=1`) y `_trazo` (la forma que van
 *  armando, calculada en cada GetMap por `vw_dibujo_en_curso`). Las alimentaba
 *  un gRPC por click, y cada click costaba un viaje de red y una tanda de
 *  tiles: el trazo se veía siempre un paso atrás de la mano. Ahora el trazo lo
 *  dibuja Leaflet, en memoria y en el acto (`AoiDrawEngine._repintarPrevia`),
 *  que es el motor rápido para lo que se está manipulando; GeoServer se queda
 *  con lo que ya está guardado, que es para lo que es rápido él. Con las capas
 *  se fueron `extenderTrazo`, `limpiarTrazo`, `refrescarTrazo`, el pane
 *  `PANE_TRAZO` y el modo `soloTrazo` del constructor, que existía sólo para
 *  que el paso 4 montara estas dos capas sin las de polígonos padre.
 *
 *  **Lo que ya no monta** es el resalte CELESTE de la pieza en edición:
 *  seleccionar una pieza ya no la repinta, lo que señala qué se está editando
 *  son sus vértices.
 */
export class AoiWmsLayer {
  /** Pane propio, ENTRE los tiles del satelital y los vectores del dibujo.
   *  Sin esto la capa competiría con el satelital dentro de `tilePane` y
   *  taparía o quedaría tapada según el orden de creación. */
  static readonly PANE = "aoiWmsPane";
  private static readonly PANE_Z_INDEX = 350;

  /** Pane propio para el TRAZO en curso (sus vértices y la forma que van
   *  armando), por ENCIMA del de los polígonos.
   *
   *  **Es un pane aparte por el paso 4.** Ahí este mismo componente monta los
   *  polígonos padre como referencia, y encima de ellos van los LOTES, con su
   *  relleno y sus etiquetas (`LotesWmsLayer`, pane 360). Con el trazo en el
   *  pane de los polígonos (350) quedaba debajo de todo eso: el usuario dibujaba
   *  y veía sus propios vértices apagados detrás del relleno de un lote.
   *
   *  En el paso 3 no cambia nada: ahí no hay lotes, y dentro de su pane el trazo
   *  ya se agregaba último, o sea encima de los polígonos. */
  /** SLD de la pieza elegida para borrar, esperando la confirmación del modal.
   *  Lo sube el configurador desde `Config.ESTILOS_EXTRA`; no es el estilo por
   *  defecto de ninguna capa, así que hay que nombrarlo en el GetMap. */
  private static readonly ESTILO_ELIMINAR = "sextante_poligono_padre_eliminar";

  /** Filtro de la capa de vértices mientras NO hay pieza seleccionada.
   *
   *  Tiene que ser un filtro que no pueda resolver a NADA, y acá estuvo un bug:
   *  el valor inicial era `poligono_padre_id IS NULL`, con el argumento de que
   *  "no resuelve a nada". Resuelve a todas las filas de TRAZO de la
   *  plataforma, que son justamente las que tienen esa columna en nulo — y sin
   *  filtrar por registro. El síntoma era que al terminar de dibujar aparecían
   *  vértices verdes (el estilo de Editar) de otro registro, desplazados
   *  respecto del polígono recién dibujado porque eran de un dibujo anterior.
   *
   *  Un uuid de ceros no es ningún `poligono_padre_id` real: los genera
   *  `Guid.CreateVersion7()`, que nunca da el nulo. */
  private static readonly PIEZA_NINGUNA = "00000000-0000-0000-0000-000000000000";

  /** Cuánto se espera a los tiles antes de soltar igual la copia de Leaflet
   *  (ver `alTerminarDePintarPadres`). Es una red de seguridad, no un tiempo
   *  esperado: el caso normal son unos pocos cientos de milisegundos. Se deja
   *  holgado a propósito —el primer repintado del registro es el más lento— y
   *  lo que se arriesga al agotarlo es el parpadeo que había siempre. */
  private static readonly ESPERA_MAXIMA_TILES_MS = 8000;

  /** **Sólo la monta el paso 3.** El paso 4 la montaba en modo `soloTrazo`
   *  —únicamente las capas del trazo en curso, sin las de polígonos padre— y
   *  esa instancia se fue con el trazo (2026-10-23).
   *
   *  **En el paso 4 el padre no se dibuja, y es un requisito del paso, no una
   *  optimización.** Ahí lo que se ve son los lotes, y el padre ES la unión de
   *  los lotes, así que pintarlo además significaba el borde del predio trazado
   *  dos veces por dos capas distintas. Peor todavía, esa capa no se refrescaba
   *  nunca en el paso 4 (se le extendía la extensión al encuadrar y nada más), así
   *  que después de mover un vértice hacia adentro seguía dibujando el contorno
   *  anterior: se veía como una línea proyectada hacia adentro del lote, que
   *  parecía un resto de Leaflet y era el padre viejo.
   *
   *  El padre se sigue GUARDANDO desde el paso 4 (lo necesitan el paso 3 y el
   *  cierre del registro), y desde el 2026-10-23 además se LEE ahí, en memoria
   *  y sin renderizar, para el snap del modo Dibujar
   *  (`LoteSplitMapRender.setPoligonosPadre`). Lo que no se hace es mostrarlo. */
  constructor(
    private readonly _map: L.Map,
    private readonly _registroId: string,
  ) {}

  /** Crea el pane y engancha las capas. Sin `registroId` no hace nada: no hay
   *  registro del que pedir ticket. */
  addTo(): void {
    if (!this._registroId || this._montado) return;
    this._montado = true;

    if (!this._map.getPane(AoiWmsLayer.PANE)) {
      const pane = this._map.createPane(AoiWmsLayer.PANE);
      pane.style.zIndex = String(AoiWmsLayer.PANE_Z_INDEX);
      // Las capas son informativas: los clicks tienen que seguir llegando al
      // mapa para poder dibujar encima.
      pane.style.pointerEvents = "none";
    }

    // El orden de alta define quién queda encima dentro del pane: primero los
    // polígonos, después el resalte de borrado, y los vértices arriba de todo
    // para que no los tape ningún relleno.
    this._padres = this._crearCapa(MAPA_CAPA_POLIGONOS_PADRE, {});
    // Nace sin pieza elegida, o sea sin pedir ningún tile.
    this._seleccion = this._crearCapa(MAPA_CAPA_POLIGONOS_PADRE, {});
    // Los vértices se filtran por PIEZA: mostrar los de todas las piezas a la
    // vez llenaría el mapa de puntos que no se pueden agarrar. Nace sin pieza
    // seleccionada, con una pieza que no existe (ver `PIEZA_NINGUNA`). La real
    // la escribe `mostrarVertices`.
    this._vertices = this._crearCapa(
      MAPA_CAPA_VERTICES,
      this._filtroVertices(AoiWmsLayer.PIEZA_NINGUNA),
    );

    // Hasta que llega el ticket del mapa ninguna capa pide tiles (ver
    // `SelectiveWms._isValidTile`); cuando llega, se repintan.
    void ticketMapaRegistro.asegurarAsync(this._registroId).then(() => {
      for (const capa of [
        this._padres,
        this._seleccion,
        this._vertices,
      ]) {
        capa?.redraw();
      }
      this._precalentar();
      // Acá recién se pidieron los primeros tiles de verdad (antes del ticket
      // no se pedía ninguno). Cuando terminen de pintarse, el mapa ya muestra
      // lo que hay guardado: es lo que espera quien tapa el paso mientras
      // tanto (ver `alPintarLoGuardado`).
      if (this._padres) {
        this._padres.alTerminarDePintar(() => this._marcarPintado());
      } else {
        this._marcarPintado();
      }
    });
  }

  /** Avisa cuando el mapa YA MUESTRA lo que había guardado al montarse: los
   *  polígonos padre del registro, pintados por el WMS.
   *
   *  **Para qué.** Quien llega al paso 3 con piezas ya guardadas —un KML recién
   *  importado, o un registro que se retoma— necesita saber cuándo dejar de
   *  tapar la pantalla. Sin esto la capa de "Importando archivo" se apagaba al
   *  montarse el paso, que es ANTES de que el ticket llegara y de que los
   *  primeros tiles estuvieran: el usuario veía el mapa vacío y los polígonos
   *  aparecían después.
   *
   *  Llama al callback **una sola vez**, y en el acto si la primera pintura ya
   *  pasó. Si no hay registro —y por lo tanto no hay nada guardado que
   *  esperar— también contesta en el acto.
   *
   *  Un fallo del ticket o de un tile cuenta como "pintado": lo que se promete
   *  es que ya no queda nada en vuelo, no que haya salido bien. Si no, un error
   *  dejaría la pantalla tapada para siempre. */
  alPintarLoGuardado(alPintar: () => void): void {
    if (this._pintado || !this._padres) {
      alPintar();
      return;
    }
    this._pendientesDePintura.push(alPintar);
  }

  private _pintado = false;
  private _pendientesDePintura: (() => void)[] = [];

  private _marcarPintado(): void {
    if (this._pintado) return;
    this._pintado = true;
    const pendientes = this._pendientesDePintura;
    this._pendientesDePintura = [];
    for (const alPintar of pendientes) alPintar();
  }

  /** Pide UNA imagen mínima de la capa de polígonos padre, nada más que para
   *  que el camino esté caliente cuando haga falta de verdad.
   *
   *  **El problema que resuelve.** En un registro nuevo esta capa nace con una
   *  extensión degenerada —no hay piezas— y por lo tanto no pide un solo tile
   *  hasta que el usuario cierra su primer polígono. Ese primer GetMap paga
   *  entonces, todo junto y con el usuario esperando, lo que después sale
   *  gratis: abrir las conexiones HTTP a la Api, el primer golpe al
   *  controlador del WMS (que se compila ahí) y la primera consulta de
   *  GeoServer a esta capa (pool de PostGIS, parseo del SLD, caché del tipo de
   *  feature). Por eso la primera pieza tardaba segundos en aparecer y las
   *  siguientes eran inmediatas — y por eso el paso 4 nunca lo sufrió: allá
   *  los lotes ya existen al entrar, así que la capa pide tiles de entrada y
   *  se calienta sola mientras el usuario mira el mapa.
   *
   *  Adelantarlo al montaje del mapa es gratis para el usuario: pasa mientras
   *  elige dónde dibujar.
   *
   *  **Va por fuera de Leaflet, a propósito**: un `Image` suelto de 1×1 px no
   *  toca el estado de ninguna capa —ni tiles, ni niveles, ni extensión— así
   *  que no puede alterar lo que se ve. Si falla no pasa nada: era una
   *  optimización, y el tile de verdad se vuelve a pedir igual. */
  private _precalentar(): void {
    const ticket = ticketMapaRegistro.actual(this._registroId);
    if (!ticket) return;

    const parametros = new URLSearchParams({
      SERVICE: "WMS",
      REQUEST: "GetMap",
      VERSION: "1.1.1",
      LAYERS: MAPA_CAPA_POLIGONOS_PADRE,
      STYLES: "",
      FORMAT: "image/png",
      TRANSPARENT: "true",
      WIDTH: "1",
      HEIGHT: "1",
      SRS: "EPSG:3857",
      BBOX: "0,0,1,1",
      TICKET: ticket,
    });

    const imagen = new Image();
    imagen.decoding = "async";
    imagen.src = `${fx_urlMapaRegistroFinca()}?${parametros.toString()}`;
  }

  private _montado = false;

  /** Muestra los vértices de UNA pieza: la que el usuario acaba de seleccionar
   *  en modo Editar.
   *
   *  Es la contraparte visual de lo que `VerticesPoligonoWfsService` trae para
   *  editar — misma tabla, mismo filtro—, y por eso los handles caen justo
   *  sobre los puntos dibujados.
   *
   *  @param bbox la caja de la pieza, para acotar los tiles a pedir. Sin ella
   *   Leaflet pediría los del viewport entero (ver `_boundsPadres`). */
  mostrarVertices(
    poligonoPadreId: string,
    bbox: [number, number, number, number],
  ): void {
    if (!this._vertices || !poligonoPadreId) return;

    this._piezaEnVertices = poligonoPadreId;
    this._vertices.setParams(
      this._filtroVertices(poligonoPadreId) as unknown as L.WMSParams,
      true,
    );
    (this._vertices.options as L.GridLayerOptions).bounds = L.latLngBounds(
      L.latLng(bbox[1], bbox[0]),
      L.latLng(bbox[3], bbox[2]),
    );
    this._vertices.refrescarTodo();
  }

  /** Apaga la capa de vértices: se soltó la pieza o se cambió de herramienta.
   *  Como en `limpiarResalte`, la extensión degenerada es lo que hace que no se
   *  pida ningún tile. */
  ocultarVertices(): void {
    if (!this._vertices) return;
    this._piezaEnVertices = null;
    // La pieza se devuelve a la que no existe ADEMÁS de degenerar la
    // extensión. Con la extensión sola alcanzaría para que no se pidan tiles
    // hoy, pero deja la capa a un `redraw()` ajeno de mostrar lo que no debe.
    this._vertices.setParams(
      this._filtroVertices(AoiWmsLayer.PIEZA_NINGUNA) as unknown as L.WMSParams,
      true,
    );
    (this._vertices.options as L.GridLayerOptions).bounds = L.latLngBounds(
      [0, 0],
      [0, 0],
    );
    this._vertices.refrescarTodo();
  }

  /** Vuelve a pedir los vértices de la pieza en edición.
   *
   *  Se llama cuando el guardado volvió: hasta ese momento la base todavía
   *  tiene los vértices donde estaban, porque los re-extrae el servidor dentro
   *  del `GuardarAoi`. Repintar antes mostraría la forma anterior. */
  refrescarVertices(bounds: L.LatLngBounds | null = null): void {
    // **Sin pieza seleccionada no se toca nada.** Esto se llamaba después de
    // CADA guardado, también al cerrar un polígono en modo Dibujar, donde no
    // hay nada que editar: ampliaba la extensión de la capa desde cero hasta el
    // área recién dibujada y la hacía pedir tiles. Con el filtro inicial mal
    // puesto, esos tiles traían vértices ajenos; con el filtro bueno traerían
    // un PNG vacío, que sigue siendo una tanda de peticiones al vacío.
    if (!this._vertices || !this._piezaEnVertices) return;
    if (!bounds) {
      this._vertices.refrescarTodo();
      return;
    }
    // La pieza puede haber crecido con el vértice movido: primero la extensión
    // —si no, Leaflet no pide los tiles nuevos— y después el repintado.
    const actual = (this._vertices.options as L.GridLayerOptions)
      .bounds as L.LatLngBounds | undefined;
    if (actual && !actual.contains(bounds)) {
      (this._vertices.options as L.GridLayerOptions).bounds = L.latLngBounds([
        actual.getSouthWest(),
        actual.getNorthEast(),
      ]).extend(bounds);
    }
    this._vertices.refrescarBounds(bounds);
  }

  /** Pinta UNA pieza en ROJO: la elegida para borrar, mientras espera la
   *  confirmación del modal.
   *
   *  Es la misma capa de polígonos padre, filtrada a una sola fila por su
   *  `indice` y con el SLD `sextante_poligono_padre_eliminar`. Un GetMap por
   *  click, acotado a los tiles que toca la pieza — por eso hace falta su
   *  `bbox`.
   *
   *  No hay resalte por HOVER: eso serían GetMap por cada `mousemove`. El
   *  amarillo que el modo Borrar pintaba al pasar el mouse se perdió con las
   *  capas de Leaflet, y ahora el resalte de Borrar es al click. */
  resaltarParaBorrar(
    indice: number,
    bbox: [number, number, number, number],
  ): Promise<void> {
    if (!this._seleccion || !this._registroId) return Promise.resolve();
    const capa = this._seleccion;

    capa.setParams(
      {
        indice: String(indice),
        styles: AoiWmsLayer.ESTILO_ELIMINAR,
      } as unknown as L.WMSParams,
      true,
    );
    (capa.options as L.GridLayerOptions).bounds = L.latLngBounds(
      L.latLng(bbox[1], bbox[0]),
      L.latLng(bbox[3], bbox[2]),
    );

    // **Se espera a que los tiles estén en pantalla.** El resalte lo pinta
    // GeoServer, así que entre el click y el rojo hay una ida y vuelta; quien
    // llama abre encima un modal de confirmación, y sin esta espera el modal
    // salía PRIMERO y el color aparecía después, debajo. El usuario tenía que
    // confirmar sin ver todavía qué pieza iba a borrar.
    //
    // `load` de Leaflet se dispara cuando terminaron de cargar todos los tiles
    // visibles de la capa. El listener se engancha ANTES de pedirlos, si no la
    // respuesta puede llegar antes que la suscripción.
    return new Promise<void>((resolve) => {
      let resuelto = false;
      const terminar = (pausa: number): void => {
        if (resuelto) return;
        resuelto = true;
        capa.off("load", alCargar);
        clearTimeout(reloj);
        if (pausa > 0) setTimeout(resolve, pausa);
        else resolve();
      };

      // El resalte ya está pintado: se deja un respiro antes de que el modal
      // lo tape (ver `PAUSA_ANTES_DEL_MODAL_MS`).
      const alCargar = (): void =>
        terminar(AoiWmsLayer.PAUSA_ANTES_DEL_MODAL_MS);

      // Red de seguridad: si GeoServer no contesta, o si el filtro no da
      // ningún tile nuevo que pedir (y entonces `load` no se dispara), el
      // modal igual tiene que abrirse. Más vale un resalte tarde que un modal
      // que nunca llega — y ahí no se suma pausa: ya se esperó de más.
      const reloj = setTimeout(() => terminar(0), AoiWmsLayer.ESPERA_RESALTE_MS);
      capa.on("load", alCargar);
      capa.refrescarTodo();
    });
  }

  /** Cuánto se espera, como mucho, a que el resalte de Borrar aparezca antes
   *  de abrir el modal. Con GeoServer respondiendo en ~25 ms por tile nunca se
   *  llega; está para que una caída no deje la acción sin modal. */
  private static readonly ESPERA_RESALTE_MS = 800;

  /** Pausa entre que el resalte aparece y que se abre el modal. GeoServer
   *  contesta en pocas decenas de ms, así que sin esto el modal tapa el color
   *  casi en el mismo cuadro en que se pintó: el usuario no llega a registrar
   *  QUÉ lote eligió. Un quinto de segundo alcanza para verlo y no se siente
   *  como una demora.
   *
   *  No se aplica cuando se resolvió por el tope de espera: ahí ya pasaron
   *  800 ms y lo que falta es abrir el modal, no esperar más. */
  private static readonly PAUSA_ANTES_DEL_MODAL_MS = 200;


  /** Saca el resalte: la capa vuelve a una extensión degenerada y deja de
   *  pedir tiles, así que la pieza queda sólo con su estilo de reposo. */
  limpiarResalte(): void {
    if (!this._seleccion) return;
    (this._seleccion.options as L.GridLayerOptions).bounds = L.latLngBounds(
      [0, 0],
      [0, 0],
    );
    this._seleccion.refrescarTodo();
  }

  /** Filtro de la capa de vértices para UNA pieza. El registro lo agrega la
   *  Api siempre, a partir del ticket: esta capa no puede mostrar filas de otro
   *  registro ni con un id que quedó viejo. */
  private _filtroVertices(poligonoPadreId: string): FiltroMapa {
    return { pieza: poligonoPadreId };
  }

  /** Arma una capa WMS con su filtro y la engancha al mapa.
   *
   *  `estilo` sólo hace falta cuando NO alcanza el estilo por defecto de la
   *  capa en GeoServer (`sextante_poligono_padre`, `sextante_dibujo_en_curso`,
   *  `sextante_vertices_poligono`). Son dos casos: los vértices del trazo, que
   *  comparten capa con los de una pieza, y el resalte de borrado. Va sin
   *  workspace: la Api lo valida contra los estilos de la capa y le pone el
   *  workspace. */
  private _crearCapa(
    capa: string,
    filtro: FiltroMapa,
    estilo: string | null = null,
    pane: string = AoiWmsLayer.PANE,
  ): SelectiveWms {
    const wms = new SelectiveWms(fx_urlMapaRegistroFinca(), {
      layers: capa,
      ...(estilo ? { styles: estilo } : {}),
      format: "image/png",
      transparent: true,
      version: "1.1.1",
      maxZoom: 20,
      pane,
      // Los datos del filtro, como parámetros sueltos (`trazo`, `pieza`,
      // `indice`). El registro no va: lo pone la Api a partir del ticket.
      ...filtro,
      uppercase: true,
      ticketMapa: () => ticketMapaRegistro.actual(this._registroId),
      // Arranca en una extensión degenerada: sin datos no se pide NINGÚN tile.
      // La amplía `extenderPadres` a medida que el usuario dibuja. Sin esto
      // Leaflet pediría todo el viewport desde el arranque.
      bounds: L.latLngBounds([0, 0], [0, 0]),
    } as L.WMSOptions & { ticketMapa: () => string });
    wms.addTo(this._map);
    return wms;
  }

  /** Escribe la extensión en la capa. Devuelve `true` si cambió.
   *
   *  Al AMPLIARSE hay tiles nuevos que antes se descartaban, y esos Leaflet
   *  sólo los evalúa en su `_update` (mover, zoom) o tras un `redraw`. Por eso
   *  el llamador repinta entero cuando esto devuelve `true`: el costo queda
   *  acotado a la extensión de los datos, que es chica, no al viewport. */
  private _aplicarBounds(
    capa: SelectiveWms | null,
    bounds: L.LatLngBounds | null,
  ): boolean {
    if (!capa || !bounds) return false;
    const opciones = capa.options as L.GridLayerOptions;
    // `options.bounds` está tipado como `LatLngBoundsExpression`, que incluye
    // el literal `[[lat, lng], [lat, lng]]` y por lo tanto no tiene `equals`.
    // Acá SIEMPRE es un `L.LatLngBounds`: lo escriben este mismo método,
    // `mostrarVertices`, `resaltarParaBorrar` y el valor inicial de
    // `_crearCapa`, todos con `L.latLngBounds(...)`. El cast dice eso; sin él
    // no compila.
    const anterior = opciones.bounds as L.LatLngBounds | undefined;
    if (anterior && anterior.equals(bounds)) return false;
    opciones.bounds = bounds;
    return true;
  }

  /** Suma un área a la extensión conocida de una capa. */
  private static _extender(
    actual: L.LatLngBounds | null,
    suma: L.LatLngBounds,
  ): L.LatLngBounds {
    return actual ? L.latLngBounds([actual.getSouthWest(), actual.getNorthEast()]).extend(suma) : suma;
  }

  /* `extenderTrazo`, `limpiarTrazo` y `refrescarTrazo` se eliminaron
     (2026-10-23) junto con las capas del trazo: el trazo en curso lo dibuja
     Leaflet y no hay tiles que extender, limpiar ni repintar por click. */

  /** Amplía la extensión de los POLÍGONOS PADRE.
   *
   *  `refrescarTodo` y no `redraw` a secas: ampliar la extensión pasa justo
   *  después de guardar una pieza, o sea cuando lo que la capa devuelve acaba
   *  de cambiar. `redraw` vuelve a pedir los tiles con la MISMA url —no sube
   *  `_v`—, así que lo único que impediría que alguno saliera de una caché
   *  sería el `no-store` de la Api. El repintado del dibujo no puede depender
   *  de un header: `refrescarTodo` sube `_v` y la url deja de ser la misma. */
  extenderPadres(bounds: L.LatLngBounds): void {
    this._boundsPadres = AoiWmsLayer._extender(this._boundsPadres, bounds);
    if (this._aplicarBounds(this._padres, this._boundsPadres)) {
      this._padres?.refrescarTodo();
    }
  }

  /** Vuelve a pedir SOLO los tiles que tocan `bounds`.
   *
   *  Después de guardar un polígono cambia el área de ese polígono y nada más.
   *  Un `redraw()` tiraría todos los tiles de la vista y se vería como una
   *  recarga completa del mapa. */
  refrescarZona(bounds: L.LatLngBounds | null): void {
    if (!this._padres) return;
    if (!bounds) {
      this._padres.refrescarTodo();
      return;
    }
    this._padres.refrescarBounds(bounds);
  }

  /** Avisa cuando la capa de polígonos padre terminó de pintar lo que se le
   *  acaba de pedir.
   *
   *  **Para qué: el relevo entre Leaflet y GeoServer.** Una pieza recién
   *  dibujada la pinta Leaflet hasta que está guardada, y de ahí en más la
   *  pinta esta capa. Si Leaflet suelta su copia en cuanto el guardado vuelve,
   *  entre ese instante y la llegada de los tiles no la pinta NADIE: el
   *  polígono desaparece y reaparece. Ese hueco dura lo que tarde el viaje
   *  Api→GeoServer→PNG, y la primera vez es el peor de todos —la capa nunca
   *  pidió un tile, así que ahí se pagan juntos la conexión HTTP nueva, el
   *  primer golpe al controlador del WMS y la primera consulta de GeoServer a
   *  esta capa—. Por eso el relevo espera a que la imagen esté.
   *
   *  Quién contesta ese momento es `SelectiveWms.alTerminarDePintar`, que mira
   *  las imágenes de los tiles y no el evento `load` de la capa: el repintado
   *  acotado escribe `src` a mano y para esos tiles `load` no se dispara.
   *
   *  **Siempre llama al callback, una sola vez.** Si una imagen quedara colgada
   *  sin `load` ni `error`, la copia de Leaflet se quedaría pegada para
   *  siempre — peor que el parpadeo. De ahí el plazo máximo: pasado ése se
   *  suelta igual.
   *
   *  Hay que llamarlo DESPUÉS de pedir el refresco: lo que se espera es lo que
   *  haya quedado en vuelo en ese momento.
   *
   *  @param alPintar Qué hacer cuando los tiles están (o se venció el plazo). */
  alTerminarDePintarPadres(alPintar: () => void): void {
    const capa = this._padres;
    if (!capa) {
      alPintar();
      return;
    }

    let resuelto = false;
    const terminar = (): void => {
      if (resuelto) return;
      resuelto = true;
      clearTimeout(reloj);
      alPintar();
    };

    const reloj = setTimeout(terminar, AoiWmsLayer.ESPERA_MAXIMA_TILES_MS);
    capa.alTerminarDePintar(terminar);
  }

  dispose(): void {
    for (const capa of [this._padres, this._vertices, this._seleccion]) {
      if (capa) this._map.removeLayer(capa);
    }
    this._padres = null;
    this._vertices = null;
    this._seleccion = null;
  }

  /** Extensión de los datos de cada capa, tal como la conoce el cliente.
   *
   *  **Esto es lo que evita las peticiones al vacío.** Leaflet NO mira el
   *  bounding box que la capa declara en GetCapabilities: `L.TileLayer` pide
   *  los tiles de todo el viewport y listo. Lo único que lo limita es
   *  `options.bounds` de `L.GridLayer`, que descarta los tiles que no la tocan
   *  (`_isValidTile`) — y hay que ponerla a mano.
   *
   *  Las capas de la pieza (vértices y resalte) no llevan una propia: su
   *  extensión es la caja de la pieza que el llamador les pasa. */
  private _boundsPadres: L.LatLngBounds | null = null;

  private _padres: SelectiveWms | null = null;
  /** Los vértices de la pieza en edición, o nada. */
  private _vertices: SelectiveWms | null = null;
  /** La pieza cuyos vértices está mostrando `_vertices`, o `null` si ninguna.
   *  Es lo que hace que `refrescarVertices` no encienda la capa cuando no hay
   *  nada seleccionado. */
  private _piezaEnVertices: string | null = null;
  /** La pieza elegida para borrar, o nada. */
  private _seleccion: SelectiveWms | null = null;
}
